import "./VideoGeneration.css";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { realisticTemplates, animeTemplates, getVideoUrl } from "../data/videoTemplatesConfig";
import { pricingTiers } from "../data/pricing";
import PaymentPopup from "../popups/PaymentPopup";
import GeneratePopup from "../popups/GeneratePopup";
import SupportEmail from "../components/SupportEmail";
import LegalNotice from "../components/LegalNotice";

// Video template card component
function VideoTemplateCard({ template, onClick }) {
  const [videoLoaded, setVideoLoaded] = useState(false);

  return (
    <div
      className="videoTemplateCard"
      onClick={() => onClick(template.id)}
    >
      {!videoLoaded && (
        <div
          className="videoTemplateMedia"
          style={{ background: "#2a1a1f" }}
        />
      )}
      <video
        src={getVideoUrl(template.video)}
        autoPlay
        loop
        muted
        playsInline
        className="videoTemplateMedia"
        style={{ display: videoLoaded ? "block" : "none" }}
        onLoadedData={() => setVideoLoaded(true)}
      />
      <div className="videoTemplateTitle">{template.title}</div>
    </div>
  );
}

// Premium banner component
function PremiumBanner({ timeLeft, onBuyClick }) {
  const premium = pricingTiers.find((t) => t.id === "premium");
  // with a first-week price the banner shows it against the regular weekly price
  const intro = premium?.intro;
  const currentPrice = intro ? intro.price : premium?.price?.current;
  const oldPrice = intro ? premium.price.current : premium?.price?.original;
  const salePercent = intro
    ? Math.round((1 - intro.price / premium.price.current) * 100)
    : premium?.sale?.percent;

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="videoGenBanner">
      <div className="videoGenBannerLeft">
        <div className="videoGenBannerPremium">PREMIUM</div>
        <div className="videoGenBannerSale">
          {salePercent}% off {intro ? "for the first week, ends in" : "sale ends in"} {formatTime(timeLeft)}
        </div>
      </div>

      <div className="videoGenBannerStats">
        <div className="videoGenBannerStat">
          <div className="videoGenBannerStatValue">{premium?.features?.photos}</div>
          <div className="videoGenBannerStatLabel">photos</div>
        </div>
        <div className="videoGenBannerStat">
          <div className="videoGenBannerStatValue">{premium?.features?.videos}</div>
          <div className="videoGenBannerStatLabel">videos</div>
        </div>
        <div className="videoGenBannerWatermark">
          <div className="videoGenBannerWatermarkIcon">
            <img src={`${process.env.PUBLIC_URL}/w-strikethrough.svg`} alt="" />
          </div>
          <div className="videoGenBannerStatLabel">no watermark</div>
        </div>
      </div>

      <div className="videoGenBannerRight">
        <div className="videoGenBannerOldPrice">
          <span className="videoGenBannerOldPriceText">${oldPrice}</span>
          <img
            src={`${process.env.PUBLIC_URL}/strike.svg`}
            alt=""
            className="videoGenBannerOldPriceStrike"
          />
        </div>
        <button className="videoGenBannerBuyButton" onClick={onBuyClick}>
          <span className="videoGenBannerBuyText">Buy for </span>
          <span className="videoGenBannerBuyPrice">${currentPrice}</span>
        </button>
      </div>
    </div>
  );
}

// Feature flag: show the "customize persona" popup (GeneratePopup) when a
// template is clicked. Mirrors site-purple's /genvideo flow — the card click
// always opens the popup and the sign-in / payment gates sit behind its
// Generate button. Flip to `false` to send clicks straight to generation.
const SHOW_CUSTOMIZE_PERSONA_POPUP = true;

export default function VideoGeneration({ token, user, setShowLoginPopup }) {
  const navigate = useNavigate();
  const [style, setStyle] = useState("realistic");
  const [timeLeft, setTimeLeft] = useState(30 * 60); // 30 minutes in seconds
  const [showPayPopUp, setShowPayPopUp] = useState(false);
  const [showGeneratePopup, setShowGeneratePopup] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  // "buy" while the premium-banner purchase waits on the sign-in popup
  const [pendingAction, setPendingAction] = useState(null);

  // Countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 30 * 60));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Pick the purchase back up once the user returns signed in.
  useEffect(() => {
    if (user && pendingAction === "buy") {
      setShowPayPopUp(true);
      setPendingAction(null);
    }
  }, [user, pendingAction]);

  const templates = style === "realistic" ? realisticTemplates : animeTemplates;

  const handleGenerate = useCallback(
    (templateId) => {
      // Check login first
      if (!user) {
        setShowGeneratePopup(false);
        if (setShowLoginPopup) setShowLoginPopup(true);
        return;
      }
      // Check payment for trial users
      if (user?.on_trial) {
        setShowGeneratePopup(false);
        setShowPayPopUp(true);
        return;
      }
      // Proceed to generation
      setShowGeneratePopup(false);
      navigate(`/video_template?template=${templateId}`);
    },
    [navigate, user, setShowLoginPopup]
  );

  const handleTemplateClick = useCallback(
    (templateId) => {
      const template = [...realisticTemplates, ...animeTemplates].find(
        (t) => t.id === templateId
      );
      setSelectedTemplate(template);
      if (SHOW_CUSTOMIZE_PERSONA_POPUP) {
        // Show the customize-persona popup first.
        setShowGeneratePopup(true);
      } else {
        // Popup disabled — go straight to generation.
        handleGenerate(templateId);
      }
    },
    [handleGenerate]
  );

  const handleCloseGeneratePopup = useCallback(() => {
    setShowGeneratePopup(false);
  }, []);

  const handleBuyClick = useCallback(() => {
    // Not signed in — sign in first, then the effect above opens the plans.
    if (!user) {
      setPendingAction("buy");
      if (setShowLoginPopup) setShowLoginPopup(true);
      return;
    }
    setShowPayPopUp(true);
  }, [user, setShowLoginPopup]);

  return (
    <div className="videoGenPage">
      {/* Header with tabs and choose template */}
      <div className="videoGenHeader">
        <div className="videoGenHeaderLeft">
          <div className="gallerySecondaryTabs">
            <button
              className={`gallerySecondaryTab ${style === "realistic" ? "gallerySecondaryTabActive" : ""}`}
              onClick={() => setStyle("realistic")}
            >
              Realistic
            </button>
            <button
              className={`gallerySecondaryTab ${style === "anime" ? "gallerySecondaryTabActive" : ""}`}
              onClick={() => setStyle("anime")}
            >
              Anime
            </button>
          </div>
        </div>
        <div className="videoGenChooseTemplate">
          <img src={`${process.env.PUBLIC_URL}/hand-pointer.svg`} alt="" />
          <span className="videoGenChooseTemplateText">Choose template for video</span>
        </div>
      </div>

      {/* Premium banner */}
      <div className="videoGenBannerSection">
        <PremiumBanner timeLeft={timeLeft} onBuyClick={handleBuyClick} />
      </div>

      {/* Video template grid */}
      <div className="videoGenGridSection">
        <div className="videoGenGrid">
          {templates.map((template) => (
            <VideoTemplateCard
              key={template.id}
              template={template}
              onClick={handleTemplateClick}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="videoGenFooter">
          <SupportEmail />
          <div className="videoGenFooterLinks">
            <div className="videoGenFooterLinkButtons">
              <a href="/terms" className="videoGenFooterLinkButton">Terms of use</a>
              <a href="/privacy" className="videoGenFooterLinkButton">Privacy policy</a>
              <a href="/take_it_down_report" className="videoGenFooterLinkButton">Report content</a>
            </div>
            {/*<div className="videoGenFooterRight">*/}
            {/*  <a*/}
            {/*    href="https://discord.gg/your-discord"*/}
            {/*    target="_blank"*/}
            {/*    rel="noopener noreferrer"*/}
            {/*    className="videoGenDiscordButton"*/}
            {/*  >*/}
            {/*    <img src={`${process.env.PUBLIC_URL}/discord.svg`} alt="" />*/}
            {/*    Discord*/}
            {/*  </a>*/}
            {/*</div>*/}
          </div>
          <LegalNotice />
        </div>
      </div>

      <PaymentPopup
        isOpen={showPayPopUp}
        setIsOpen={setShowPayPopUp}
        token={token}
      />

      <GeneratePopup
        template={selectedTemplate}
        isOpen={showGeneratePopup}
        onClose={handleCloseGeneratePopup}
        onGenerate={handleGenerate}
      />
    </div>
  );
}
